import React, { useState, useEffect } from 'react';
import {
  Card, Row, Col, Button, Table, Modal, Form, Input, InputNumber,
  Select, Tag, Space, Spin, Alert, Statistic, Progress
} from 'antd';
import {
  PlusOutlined, DeleteOutlined, ExclamationCircleOutlined,
  EditOutlined, BarChartOutlined, CheckCircleOutlined, WarningOutlined
} from '@ant-design/icons';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell
} from 'recharts';
import { useAuthStore } from '../store/authStore';
import { useAuth } from '../../../context/UnifiedAuthContext';
import { apiService } from '../services/api';
import type { Budget, BudgetLineItem, BudgetStatus } from '../types';
import { staticApi } from '../utils/staticApi';
import { currency } from '../utils/currency';   // Sprint 146 (R14-11)

const { Option } = Select;

const STATUS_CONFIG: Record<BudgetStatus, { color: string; label: string }> = {
  DRAFT:  { color: 'default', label: 'Draft' },
  ACTIVE: { color: 'green',   label: 'Active' },
  CLOSED: { color: 'volcano', label: 'Closed' },
};

const EXPENSE_CATEGORIES = [
  'maintenance', 'utilities', 'staff', 'insurance', 'repairs', 'other'
];

export const BudgetPage: React.FC = () => {
  const { currentCondo } = useAuthStore();
  const { user } = useAuth();
  const canManage = user?.role === 'super_admin_condo' || user?.role === 'admin';

  const [budgets, setBudgets] = useState<Budget[]>([]);
  const [selectedBudgetId, setSelectedBudgetId] = useState<string | null>(null);
  const [lineItems, setLineItems] = useState<BudgetLineItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [lineLoading, setLineLoading] = useState(false);

  const [budgetModalOpen, setBudgetModalOpen] = useState(false);
  const [editingBudget, setEditingBudget] = useState<Budget | null>(null);
  const [lineModalOpen, setLineModalOpen] = useState(false);
  const [editingLine, setEditingLine] = useState<BudgetLineItem | null>(null);
  const [budgetForm] = Form.useForm();
  const [lineForm] = Form.useForm();

  const condoId = currentCondo?.id;
  const selectedBudget = budgets.find(b => b.id === selectedBudgetId);

  useEffect(() => {
    if (!condoId) return;
    loadBudgets();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- condo-scoped fetch-on-mount; loadBudgets closes over condoId already in deps
  }, [condoId]);

  const loadBudgets = async () => {
    if (!condoId) return;
    setLoading(true);
    try {
      const data = await apiService.getBudgets(condoId);
      setBudgets(data);
      if (data.length > 0 && !selectedBudgetId) {
        setSelectedBudgetId(data[0].id!);
        loadLineItems(data[0].id!);
      } else if (selectedBudgetId) {
        loadLineItems(selectedBudgetId);
      }
    } catch {
      staticApi.message.error('Failed to load budgets');
    } finally {
      setLoading(false);
    }
  };

  const loadLineItems = async (budgetId: string) => {
    setLineLoading(true);
    try {
      const data = await apiService.getBudgetLineItems(budgetId);
      setLineItems(data);
    } catch {
      staticApi.message.error('Failed to load line items');
    } finally {
      setLineLoading(false);
    }
  };

  const handleSelectBudget = (id: string) => {
    setSelectedBudgetId(id);
    loadLineItems(id);
  };

  const handleSaveBudget = async (values: any) => {
    if (!condoId) return;
    try {
      if (editingBudget?.id) {
        await apiService.updateBudget(editingBudget.id, { ...values, condoId });
        staticApi.message.success('Budget updated');
      } else {
        await apiService.createBudget({ ...values, condoId });
        staticApi.message.success('Budget created');
      }
      setBudgetModalOpen(false);
      setEditingBudget(null);
      budgetForm.resetFields();
      loadBudgets();
    } catch {
      staticApi.message.error('Failed to save budget');
    }
  };

  const handleDeleteBudget = (budget: Budget) => {
    staticApi.modal.confirm({
      title: `Delete "${budget.name}"?`,
      icon: <ExclamationCircleOutlined />,
      content: 'All line items will also be deleted.',
      okType: 'danger',
      async onOk() {
        await apiService.deleteBudget(budget.id!);
        staticApi.message.success('Budget deleted');
        if (selectedBudgetId === budget.id) { setSelectedBudgetId(null); setLineItems([]); }
        loadBudgets();
      },
    });
  };

  const handleSaveLine = async (values: any) => {
    if (!selectedBudgetId || !condoId) return;
    try {
      if (editingLine?.id) {
        await apiService.updateBudgetLineItem(editingLine.id, values);
        staticApi.message.success('Line item updated');
      } else {
        await apiService.addBudgetLineItem(selectedBudgetId, {
          ...values,
          budgetId: selectedBudgetId,
          condoId,
        });
        staticApi.message.success('Line item added');
      }
      setLineModalOpen(false);
      setEditingLine(null);
      lineForm.resetFields();
      loadLineItems(selectedBudgetId);
    } catch {
      staticApi.message.error('Failed to save line item');
    }
  };

  const handleDeleteLine = (item: BudgetLineItem) => {
    staticApi.modal.confirm({
      title: 'Delete this budget line?',
      okType: 'danger',
      async onOk() {
        await apiService.deleteBudgetLineItem(item.id!);
        staticApi.message.success('Line item deleted');
        loadLineItems(selectedBudgetId!);
      },
    });
  };

  // Summary calculations
  const totalBudgeted = lineItems.reduce((s, i) => s + Number(i.budgetedAmount ?? 0), 0);
  const totalActual   = lineItems.reduce((s, i) => s + Number(i.actualAmount  ?? 0), 0);
  const totalVariance = totalBudgeted - totalActual;
  const consumed      = totalBudgeted > 0 ? (totalActual / totalBudgeted) * 100 : 0;

  // Chart data
  const chartData = lineItems.map(i => ({
    category: i.category.charAt(0).toUpperCase() + i.category.slice(1),
    Budgeted: Number(i.budgetedAmount),
    Actual:   Number(i.actualAmount ?? 0),
  }));

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 28, fontWeight: 700, margin: 0 }}>Budget Management</h1>
          <p style={{ color: '#666', margin: 0 }}>Annual budgets and expense variance tracking</p>
        </div>
        {canManage && (
          <Button type="primary" icon={<PlusOutlined />} onClick={() => {
            setEditingBudget(null);
            budgetForm.resetFields();
            budgetForm.setFieldsValue({ fiscalYear: new Date().getFullYear(), status: 'DRAFT' });
            setBudgetModalOpen(true);
          }}>
            New Budget
          </Button>
        )}
      </div>

      {budgets.length === 0 && !loading && (
        <Alert type="info" showIcon message="No budgets found. Create your first annual budget to start tracking expenses against targets." style={{ marginBottom: 24 }} />
      )}

      <Spin spinning={loading}>
        {/* Budget selector cards */}
        <Row gutter={[12, 12]} style={{ marginBottom: 24 }}>
          {budgets.map(b => (
            <Col xs={24} sm={12} md={8} lg={6} key={b.id}>
              <Card
                hoverable
                size="small"
                onClick={() => handleSelectBudget(b.id!)}
                style={{
                  borderRadius: 10,
                  border: selectedBudgetId === b.id ? '2px solid #6366f1' : '1px solid #e8e8e8',
                  cursor: 'pointer',
                }}
                extra={canManage && (
                  <Space onClick={e => e.stopPropagation()}>
                    <Button size="small" icon={<EditOutlined />} onClick={() => {
                      setEditingBudget(b);
                      budgetForm.setFieldsValue(b);
                      setBudgetModalOpen(true);
                    }} />
                    <Button size="small" danger icon={<DeleteOutlined />} onClick={() => handleDeleteBudget(b)} />
                  </Space>
                )}
              >
                <div style={{ fontWeight: 600 }}>{b.name}</div>
                <div style={{ color: '#888', fontSize: 12 }}>FY {b.fiscalYear}</div>
                <Tag color={STATUS_CONFIG[b.status]?.color} style={{ marginTop: 4 }}>
                  {STATUS_CONFIG[b.status]?.label}
                </Tag>
              </Card>
            </Col>
          ))}
        </Row>

        {selectedBudget && (
          <>
            {/* KPI Summary Row */}
            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
              <Col xs={24} sm={8}>
                <Card bordered style={{ borderRadius: 10 }}>
                  <Statistic title="Total Budgeted" value={totalBudgeted} precision={2} prefix="$" valueStyle={{ color: '#6366f1' }} />
                </Card>
              </Col>
              <Col xs={24} sm={8}>
                <Card bordered style={{ borderRadius: 10 }}>
                  <Statistic
                    title="Total Actual Spend"
                    value={totalActual}
                    precision={2}
                    prefix="$"
                    valueStyle={{ color: totalActual > totalBudgeted ? '#f5222d' : '#52c41a' }}
                  />
                  <Progress percent={Math.min(consumed, 100)} strokeColor={consumed > 100 ? '#f5222d' : consumed > 80 ? '#faad14' : '#52c41a'} size="small" style={{ marginTop: 8 }} />
                </Card>
              </Col>
              <Col xs={24} sm={8}>
                <Card bordered style={{ borderRadius: 10 }}>
                  <Statistic
                    title="Variance (Remaining)"
                    value={Math.abs(totalVariance)}
                    precision={2}
                    prefix={totalVariance >= 0 ? <CheckCircleOutlined style={{ color: '#52c41a' }} /> : <WarningOutlined style={{ color: '#f5222d' }} />}
                    valueStyle={{ color: totalVariance >= 0 ? '#52c41a' : '#f5222d' }}
                    suffix={totalVariance >= 0 ? 'under' : 'over'}
                  />
                </Card>
              </Col>
            </Row>

            {/* Chart */}
            {chartData.length > 0 && (
              <Card title={<Space><BarChartOutlined /> Budget vs. Actual by Category</Space>} style={{ marginBottom: 24, borderRadius: 10 }}>
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={chartData} margin={{ top: 10, right: 20, left: 0, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="category" />
                    <YAxis tickFormatter={v => `$${v.toLocaleString()}`} />
                    <Tooltip formatter={(v: any) => currency(v)} />
                    <Legend />
                    <Bar dataKey="Budgeted" fill="#6366f1" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="Actual" radius={[4, 4, 0, 0]}>
                      {chartData.map((entry, idx) => (
                        <Cell key={idx} fill={entry.Actual > entry.Budgeted ? '#f5222d' : '#52c41a'} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Card>
            )}

            {/* Line Items Table */}
            <Card
              title={`${selectedBudget.name} — Line Items`}
              extra={canManage && (
                <Button type="primary" icon={<PlusOutlined />} size="small" onClick={() => {
                  setEditingLine(null);
                  lineForm.resetFields();
                  setLineModalOpen(true);
                }}>
                  Add Line
                </Button>
              )}
              style={{ borderRadius: 10 }}
            >
              <Spin spinning={lineLoading}>
                <Table
                  rowKey="id"
                  dataSource={lineItems}
                  pagination={false}
                  columns={[
                    {
                      title: 'Category',
                      dataIndex: 'category',
                      key: 'category',
                      render: (v: string) => <Tag color="blue">{v.charAt(0).toUpperCase() + v.slice(1)}</Tag>,
                    },
                    { title: 'Description', dataIndex: 'description', key: 'description', render: (v: string) => v || '—' },
                    {
                      title: 'Budgeted',
                      dataIndex: 'budgetedAmount',
                      key: 'budgetedAmount',
                      render: (v: number) => currency(v),   // Sprint 146 (R14-11)
                      align: 'right' as const,
                    },
                    {
                      title: 'Actual',
                      dataIndex: 'actualAmount',
                      key: 'actualAmount',
                      render: (v: number) => currency(v),   // Sprint 146 (R14-11)
                      align: 'right' as const,
                    },
                    {
                      title: 'Variance',
                      dataIndex: 'variance',
                      key: 'variance',
                      render: (v: number) => {
                        const isOver = (v ?? 0) < 0;
                        return (
                          <span style={{ color: isOver ? '#f5222d' : '#52c41a', fontWeight: 600 }}>
                            {isOver ? '▲ Over' : '▼ Under'} ${Math.abs(Number(v ?? 0)).toFixed(2)}
                          </span>
                        );
                      },
                      align: 'right' as const,
                    },
                    {
                      title: '%',
                      dataIndex: 'variancePercent',
                      key: 'variancePercent',
                      render: (v: number) => {
                        const isOver = (v ?? 0) < 0;
                        return <Tag color={isOver ? 'red' : 'green'}>{Math.abs(Number(v ?? 0)).toFixed(1)}%</Tag>;
                      },
                    },
                    ...(canManage ? [{
                      title: '',
                      key: 'actions',
                      render: (_: any, record: BudgetLineItem) => (
                        <Space>
                          <Button size="small" icon={<EditOutlined />} onClick={() => {
                            setEditingLine(record);
                            lineForm.setFieldsValue(record);
                            setLineModalOpen(true);
                          }} />
                          <Button size="small" danger icon={<DeleteOutlined />} onClick={() => handleDeleteLine(record)} />
                        </Space>
                      ),
                    }] : []),
                  ]}
                  summary={() => (
                    <Table.Summary.Row style={{ fontWeight: 700, background: '#fafafa' }}>
                      <Table.Summary.Cell index={0} colSpan={2}>Total</Table.Summary.Cell>
                      <Table.Summary.Cell index={2} align="right">${totalBudgeted.toFixed(2)}</Table.Summary.Cell>
                      <Table.Summary.Cell index={3} align="right">${totalActual.toFixed(2)}</Table.Summary.Cell>
                      <Table.Summary.Cell index={4} align="right">
                        <span style={{ color: totalVariance >= 0 ? '#52c41a' : '#f5222d' }}>
                          {totalVariance >= 0 ? '▼' : '▲'} ${Math.abs(totalVariance).toFixed(2)}
                        </span>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={5} />
                    </Table.Summary.Row>
                  )}
                />
              </Spin>
            </Card>
          </>
        )}
      </Spin>

      {/* Budget Modal */}
      <Modal
        title={editingBudget ? 'Edit Budget' : 'New Budget'}
        open={budgetModalOpen}
        onCancel={() => { setBudgetModalOpen(false); setEditingBudget(null); budgetForm.resetFields(); }}
        footer={null}
      >
        <Form form={budgetForm} layout="vertical" onFinish={handleSaveBudget}>
          <Form.Item name="fiscalYear" label="Fiscal Year" rules={[{ required: true }]}>
            <InputNumber min={2000} max={2100} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="name" label="Budget Name" rules={[{ required: true }]}>
            <Input placeholder="e.g., 2026 Annual Operating Budget" />
          </Form.Item>
          <Form.Item name="status" label="Status" rules={[{ required: true }]}>
            <Select>
              <Option value="DRAFT">Draft</Option>
              <Option value="ACTIVE">Active</Option>
              <Option value="CLOSED">Closed</Option>
            </Select>
          </Form.Item>
          <Form.Item name="notes" label="Notes">
            <Input.TextArea rows={2} />
          </Form.Item>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Button onClick={() => { setBudgetModalOpen(false); budgetForm.resetFields(); }}>Cancel</Button>
            <Button type="primary" htmlType="submit">{editingBudget ? 'Update' : 'Create'}</Button>
          </div>
        </Form>
      </Modal>

      {/* Line Item Modal */}
      <Modal
        title={editingLine ? 'Edit Line Item' : 'Add Budget Line'}
        open={lineModalOpen}
        onCancel={() => { setLineModalOpen(false); setEditingLine(null); lineForm.resetFields(); }}
        footer={null}
      >
        <Form form={lineForm} layout="vertical" onFinish={handleSaveLine}>
          <Form.Item name="category" label="Expense Category" rules={[{ required: true }]}>
            <Select>
              {EXPENSE_CATEGORIES.map(c => (
                <Option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input placeholder="Optional — more specific description" />
          </Form.Item>
          <Form.Item name="budgetedAmount" label="Budgeted Amount ($)" rules={[{ required: true }]}>
            <InputNumber min={0} step={100} prefix="$" style={{ width: '100%' }} />
          </Form.Item>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Button onClick={() => { setLineModalOpen(false); lineForm.resetFields(); }}>Cancel</Button>
            <Button type="primary" htmlType="submit">{editingLine ? 'Update' : 'Add'}</Button>
          </div>
        </Form>
      </Modal>
    </div>
  );
};
